import React, { useEffect, useState, useRef, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HeartIcon as Heart, PaperPlaneRightIcon as PaperPlaneRight, ImageIcon as ImageIcon, XIcon as X, FlagIcon as Flag, DotsThreeVerticalIcon as DotsThreeVertical, TrashIcon as Trash, EyeSlashIcon as EyeSlash, EyeClosedIcon as EyeClosed, EyeIcon as Eye, PencilSimpleIcon as PencilSimple, PawPrintIcon as PawPrint, FilmStripIcon as FilmStrip, ClockCounterClockwiseIcon as ClockCounterClockwise, ProhibitIcon as Prohibit, TrophyIcon as Trophy, SmileyIcon as Smiley, ShareNetworkIcon as ShareNetwork, TagIcon as Tag } from "@phosphor-icons/react";
import { nativeShare } from "@/lib/native";

const REACTION_LIST = [
  { key: "paw", Icon: PawPrint, label: "Paw" },
  { key: "love", Icon: Heart, label: "Love" },
  { key: "champion", Icon: Trophy, label: "Champion" },
  { key: "laugh", Icon: Smiley, label: "Laugh" },
];
import { toast } from "sonner";
import { formatApiError, thumbUrl, photoUrl } from "@/lib/utils-app";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TEXT_MAX = 500;
const PHOTO_MAX = 4;
const VIDEO_MAX_SEC = 10;
const VIDEO_MAX_BYTES = 25 * 1024 * 1024;

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

function PhotoGrid({ photos = [], onClick }) {
  if (photos.length === 0) return null;
  const cls =
    photos.length === 1
      ? "grid grid-cols-1"
      : photos.length === 2
      ? "grid grid-cols-2 gap-1"
      : "grid grid-cols-2 gap-1";
  return (
    <div className={`${cls} mt-3 rounded-xl overflow-hidden`}>
      {photos.map((p, i) => (
        <button
          key={p + i}
          type="button"
          onClick={() => onClick && onClick(i)}
          className={`block bg-[#F5F5F5] ${
            photos.length === 3 && i === 0 ? "row-span-2" : ""
          }`}
          data-testid={`post-photo-${i}`}
        >
          <img
            src={thumbUrl(p)}
            alt=""
            className="w-full h-full object-cover aspect-square"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}

function Composer({ ownedDogs, onPosted, prefillDogId, prefillText, requireGuidelinesAccept }) {
  const [dogId, setDogId] = useState("");
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null); // { storage_path, thumb_path, duration_sec }
  const [musicOk, setMusicOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const rootRef = useRef(null);

  // Apply prefill from URL params (?dog_id=…&text=…) sent by the
  // "Share to Pawprints" button on the dog detail page. Only fills empty
  // fields so the user's in-progress draft isn't clobbered.
  useEffect(() => {
    if (prefillDogId && ownedDogs.some((d) => d.id === prefillDogId)) {
      setDogId(prefillDogId);
    } else if (!dogId && ownedDogs.length > 0) {
      setDogId(ownedDogs[0].id);
    }
  }, [ownedDogs, prefillDogId, dogId]);

  useEffect(() => {
    if (prefillText && !text) {
      setText(prefillText.slice(0, TEXT_MAX));
      // Scroll the composer into view so the user sees the pre-filled draft.
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Run only when prefill arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillText]);

  const onPhotosPick = async (e) => {
    const remaining = PHOTO_MAX - photos.length;
    const files = Array.from(e.target.files || []).slice(0, remaining);
    if (files.length === 0) return;
    // Track successful uploads so a single failure mid-batch doesn't drop the
    // ones that already succeeded. Also surface a per-file error toast so the
    // user knows exactly which photo failed (file size? format?) instead of
    // a silent "post some, lose some" experience.
    const succeeded = [];
    const failures = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        succeeded.push(data.storage_path);
      } catch (err) {
        failures.push({ name: f.name || "photo", err });
      }
    }
    if (succeeded.length > 0) {
      setPhotos((p) => [...p, ...succeeded]);
    }
    for (const fail of failures) {
      toast.error(`${fail.name}: ${formatApiError(fail.err)}`);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  /** Probe a chosen video file's duration in the browser before
   *  uploading. Reject anything > 10s up front so the user gets immediate
   *  feedback (and we don't waste bandwidth). */
  const probeDuration = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(v.duration);
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      v.src = url;
    });

  const onVideoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > VIDEO_MAX_BYTES) {
      toast.error("Video is over 25 MB. Compress it before posting.");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    const dur = await probeDuration(f);
    if (dur === null) {
      toast.error("Couldn't read this video. Try a different file.");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    if (dur > VIDEO_MAX_SEC + 0.5) {
      toast.error(`Videos must be ${VIDEO_MAX_SEC} seconds or less (this one is ${dur.toFixed(1)}s).`);
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideo({
        storage_path: data.storage_path,
        thumb_path: data.thumb_path,
        duration_sec: data.duration_sec,
      });
      // Picking a video clears any photos — they're mutually exclusive.
      setPhotos([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingVideo(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!dogId) {
      toast.error("Pick a dog to post as.");
      return;
    }
    if (!text.trim()) {
      toast.error("Write something first.");
      return;
    }
    if (video && !musicOk) {
      toast.error("Confirm the video has no copyrighted music before posting.");
      return;
    }
    // First-time-poster gate. If the user hasn't agreed to the Pawprints
    // community guidelines yet, the parent shows a dialog and only invokes
    // `runSubmit` if/when the user accepts.
    const runSubmit = async () => {
      setBusy(true);
      try {
        const payload = {
          dog_id: dogId,
          text: text.trim(),
          photos: video ? [] : photos,
        };
        if (video) {
          payload.video_path = video.storage_path;
          payload.video_poster = video.thumb_path;
          payload.music_attestation = true;
        }
        const { data } = await api.post("/posts", payload);
        onPosted(data);
        setText("");
        setPhotos([]);
        setVideo(null);
        setMusicOk(false);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setBusy(false);
      }
    };
    if (requireGuidelinesAccept) {
      const allowed = await requireGuidelinesAccept();
      if (!allowed) return; // user dismissed the gate
    }
    await runSubmit();
  };

  if (ownedDogs.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-6 text-center mb-6">
        <PawPrint size={32} weight="duotone" className="text-gold mx-auto mb-2" />
        <p className="text-sm font-semibold text-ink">Register a dog to start posting</p>
        <p className="text-xs text-[#6B6B6B] mt-1">
          Posts are written from the dog's perspective. Add a dog to your registry first.
        </p>
      </div>
    );
  }

  const selected = ownedDogs.find((d) => d.id === dogId) || ownedDogs[0];
  const dogPhoto = selected?.photos?.[0];

  return (
    <div ref={rootRef} className="bg-white border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 mb-6 shadow-[0_8px_30px_rgba(14,14,14,0.04)]" data-testid="composer">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[#FAFAFA] border border-[#E5E5E5] overflow-hidden flex-shrink-0">
          {dogPhoto ? (
            <img src={thumbUrl(dogPhoto)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <PawPrint size={20} weight="duotone" className="text-[#6B6B6B]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Select value={dogId} onValueChange={setDogId}>
            <SelectTrigger className="h-8 w-auto inline-flex border-none bg-[#FAFAFA] rounded-full text-sm font-semibold text-ink hover:bg-[#F0F0F0]" data-testid="composer-dog-select">
              <SelectValue placeholder="Pick a dog…" />
            </SelectTrigger>
            <SelectContent>
              {ownedDogs.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} <span className="text-[#6B6B6B] ml-1">· {d.breed}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
            placeholder={`What's on ${selected?.name || "your dog"}'s mind?`}
            rows={3}
            className="mt-2 border-none resize-none text-base focus-visible:ring-0 px-0"
            data-testid="composer-textarea"
          />
          {photos.length > 0 && !video && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {photos.map((p, i) => (
                <div key={p + i} className="relative aspect-square rounded-lg overflow-hidden bg-[#F5F5F5]">
                  <img src={thumbUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white hover:bg-black"
                    data-testid={`composer-remove-photo-${i}`}
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {video && (
            <div className="mt-3 space-y-3" data-testid="composer-video-preview">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-black max-w-md">
                <video
                  src={photoUrl(video.storage_path)}
                  poster={video.thumb_path ? photoUrl(video.thumb_path) : undefined}
                  className="w-full h-full object-contain"
                  muted
                  playsInline
                  controls
                />
                <button
                  type="button"
                  onClick={() => { setVideo(null); setMusicOk(false); }}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-white hover:bg-black"
                  data-testid="composer-remove-video"
                >
                  <X size={12} weight="bold" />
                </button>
                <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 text-white text-[10px] font-bold rounded-full">
                  {video.duration_sec ? `${video.duration_sec.toFixed(1)}s` : "Video"}
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-[#3F3F3F] cursor-pointer max-w-md">
                <input
                  type="checkbox"
                  checked={musicOk}
                  onChange={(e) => setMusicOk(e.target.checked)}
                  className="mt-0.5 accent-[#C9A227]"
                  data-testid="composer-music-attestation"
                />
                <span>
                  I confirm this video contains <strong>no copyrighted music</strong>.
                  Videos on The Dog's Voice play <strong>muted by default</strong> in the feed
                  to keep things friendly and copyright-safe.
                </span>
              </label>
            </div>
          )}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onPhotosPick}
                data-testid="composer-photo-input"
              />
              <input
                ref={videoRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/*"
                className="hidden"
                onChange={onVideoPick}
                data-testid="composer-video-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={photos.length >= PHOTO_MAX || !!video}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-gold hover:bg-[#FBF7EC] disabled:text-[#CCC] disabled:hover:bg-transparent transition-colors text-xs font-semibold"
                title={
                  video
                    ? "Remove the video to add photos"
                    : photos.length >= PHOTO_MAX
                    ? "Up to 4 photos"
                    : photos.length === 0
                    ? "Add up to 4 photos"
                    : `Add another photo (${photos.length} of ${PHOTO_MAX})`
                }
                data-testid="composer-add-photo-btn"
              >
                <ImageIcon size={18} weight="duotone" />
                <span>
                  {photos.length === 0 ? "Photo" : `Photo · ${photos.length}/${PHOTO_MAX}`}
                </span>
              </button>
              <button
                type="button"
                onClick={() => videoRef.current?.click()}
                disabled={uploadingVideo || photos.length > 0 || !!video}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-gold hover:bg-[#FBF7EC] disabled:text-[#CCC] disabled:hover:bg-transparent transition-colors text-xs font-semibold"
                title={
                  photos.length > 0
                    ? "Remove photos first to add a video"
                    : video
                    ? "Remove the current video to upload another"
                    : `Add a ${VIDEO_MAX_SEC}-second video clip`
                }
                data-testid="composer-add-video-btn"
              >
                <FilmStrip size={18} weight="duotone" />
                <span>{uploadingVideo ? "Uploading…" : `Video · ${VIDEO_MAX_SEC}s`}</span>
              </button>
              <span className={`text-xs ${text.length > TEXT_MAX - 50 ? "text-[#C1272D] font-semibold" : "text-[#6B6B6B]"}`}>
                {TEXT_MAX - text.length}
              </span>
            </div>
            <Button
              onClick={submit}
              disabled={busy || uploadingVideo || !text.trim() || !dogId || (video && !musicOk)}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-9 px-5"
              data-testid="composer-submit-btn"
            >
              <PaperPlaneRight size={14} className="mr-1" weight="duotone" />
              {busy ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, currentUser, onChanged, isFollowing, onToggleFollow }) {
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.text);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null); // null = loading, [] = loaded empty
  const isOwner = post.viewer?.is_owner;
  const isAdmin = currentUser?.role === "admin";
  const liked = post.viewer?.has_liked;

  const react = async (reaction) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/like`, { reaction });
      onChanged({
        ...post,
        like_count: data.like_count,
        reaction_counts: data.reaction_counts || {},
        viewer: { ...post.viewer, has_liked: data.liked, reaction: data.reaction },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sharePost = async () => {
    const url = `${webOrigin()}/p/${post.id}`;
    const shared = await nativeShare({
      title: `${post.dog?.name || "A dog"} on The Dog's Voice`,
      text: (post.text || "").slice(0, 120),
      url,
    });
    if (!shared) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — share it anywhere!");
      } catch {
        toast.error("Could not copy the link");
      }
    }
  };

  const removePost = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onChanged(null);
      toast.success("Post deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitEdit = async () => {
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { text: editText.trim() });
      onChanged(data);
      setEditing(false);
      toast.success("Post updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await api.post(`/posts/${post.id}/report`, { reason: reportReason.trim() });
      toast.success("Reported. An admin will review.");
      setReportOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const blockAuthor = async () => {
    if (!post.author_user_id) return;
    if (!window.confirm("Block this dog's owner? You won't see their posts, and they can't message you. You can unblock in Settings → Security.")) return;
    try {
      await api.post(`/users/${post.author_user_id}/block`);
      onChanged(null);
      toast.success("User blocked.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const adminHide = async () => {
    try {
      await api.post(`/admin/posts/${post.id}/${post.hidden_by_admin ? "unhide" : "hide"}`);
      onChanged({ ...post, hidden_by_admin: !post.hidden_by_admin });
      toast.success(post.hidden_by_admin ? "Post unhidden." : "Post hidden.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistory(null);
    try {
      const { data } = await api.get(`/admin/posts/${post.id}/edit-history`);
      setHistory(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setHistory({ post: null, entries: [] });
    }
  };

  return (
    <article
      className={`bg-white border border-[#E5E5E5] rounded-2xl p-4 sm:p-5 shadow-[0_8px_30px_rgba(14,14,14,0.04)] ${
        post.hidden_by_admin ? "opacity-60 border-dashed" : ""
      }`}
      data-testid={`post-${post.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[#FAFAFA] border border-[#E5E5E5] overflow-hidden flex-shrink-0">
          {post.dog.photo ? (
            <img src={thumbUrl(post.dog.photo)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <PawPrint size={20} weight="duotone" className="text-[#6B6B6B]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-spectral text-base text-ink truncate">
              {post.dog.name}
            </p>
            <span className="text-xs text-[#6B6B6B] truncate">
              · {post.dog.breed} · {timeAgo(post.created_at)}
              {post.updated_at && post.updated_at !== post.created_at ? " · edited" : ""}
            </span>
            {post.hidden_by_admin && (
              <span className="inline-flex items-center bg-[#C1272D]/10 text-crimson text-[10px] font-bold rounded-full px-2 py-0.5">
                <EyeSlash size={10} className="mr-1" weight="bold" />
                HIDDEN
              </span>
            )}
          </div>
          {editing ? (
            <div className="mt-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value.slice(0, TEXT_MAX))}
                rows={3}
                data-testid={`post-${post.id}-edit-textarea`}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setEditing(false); setEditText(post.text); }}>
                  Cancel
                </Button>
                <Button size="sm" className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" onClick={submitEdit}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-[15px] text-ink whitespace-pre-wrap">{post.text}</p>
          )}
          {!editing && post.video_path && (
            <div className="mt-3 rounded-xl overflow-hidden bg-black max-w-md" data-testid={`post-${post.id}-video`}>
              <video
                src={photoUrl(post.video_path)}
                poster={post.video_poster ? photoUrl(post.video_poster) : undefined}
                className="w-full aspect-video object-contain bg-black"
                muted
                autoPlay
                loop
                playsInline
                controls
                preload="metadata"
              />
            </div>
          )}
          {!editing && !post.video_path && <PhotoGrid photos={post.photos} />}
          <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#E5E5E5] flex-wrap">
            {REACTION_LIST.map(({ key, Icon, label }) => {
              const count = post.reaction_counts?.[key] || 0;
              const active = liked && post.viewer?.reaction === key;
              return (
                <button
                  key={key}
                  type="button"
                  title={label}
                  onClick={() => react(key)}
                  disabled={busy || post.hidden_by_admin}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-sm transition-colors ${
                    active ? "bg-[#C1272D]/10 text-[#C1272D] font-semibold" : "text-[#6B6B6B] hover:bg-[#FAFAFA]"
                  } disabled:opacity-50`}
                  data-testid={`post-${post.id}-react-${key}`}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  <span className="hidden sm:inline text-[12px]">{label}</span>
                  {count > 0 ? count : ""}
                </button>
              );
            })}
            {post.sharing_allowed !== false && (
              <button
                type="button"
                onClick={sharePost}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-sm text-[#6B6B6B] hover:bg-[#FAFAFA]"
                title="Share"
                data-testid={`post-${post.id}-share-btn`}
              >
                <ShareNetwork size={16} />
              </button>
            )}
            {post.dog?.for_sale && !isOwner && (
              <a
                href={`/dogs/${post.dog.id}`}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold bg-[#0E6B3A]/10 text-[#0E6B3A] hover:bg-[#0E6B3A]/20"
                data-testid={`post-${post.id}-inquire-chip`}
              >
                <Tag size={12} weight="fill" />
                Available{post.dog.asking_price ? ` · $${Number(post.dog.asking_price).toLocaleString()}` : ""} — Inquire
              </a>
            )}
            {!isOwner && post.author_user_id && (
              <button
                type="button"
                onClick={() => onToggleFollow?.(post.author_user_id)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold transition-colors ${
                  isFollowing ? "bg-ink/5 text-[#6B6B6B]" : "bg-[#0E0E0E] text-white hover:bg-[#C1272D]"
                }`}
                data-testid={`post-${post.id}-follow-btn`}
              >
                {isFollowing ? "Following ✓" : "+ Follow"}
              </button>
            )}
            <div className="ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="p-1.5 rounded-full text-[#6B6B6B] hover:bg-[#FAFAFA]"
                    data-testid={`post-${post.id}-menu-btn`}
                    aria-label="Post actions"
                  >
                    <DotsThreeVertical size={16} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {(isOwner || isAdmin) && (
                    <DropdownMenuItem onClick={() => setEditing(true)}>
                      <PencilSimple size={14} className="mr-2" />
                      Edit{isAdmin && !isOwner ? " (admin)" : ""}
                    </DropdownMenuItem>
                  )}
                  {(isOwner || isAdmin) && (
                    <DropdownMenuItem onClick={removePost} className="text-crimson">
                      <Trash size={14} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={adminHide}>
                      {post.hidden_by_admin ? <Eye size={14} className="mr-2" /> : <EyeClosed size={14} className="mr-2" />}
                      {post.hidden_by_admin ? "Unhide (admin)" : "Hide (admin)"}
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={openHistory} data-testid={`post-${post.id}-history-btn`}>
                      <ClockCounterClockwise size={14} className="mr-2" />
                      Edit history (admin)
                    </DropdownMenuItem>
                  )}
                  {!isOwner && (
                    <>
                      {(isOwner || isAdmin) && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={() => setReportOpen(true)}>
                        <Flag size={14} className="mr-2" />
                        Report
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={blockAuthor} className="text-crimson" data-testid={`post-${post.id}-block-btn`}>
                        <Prohibit size={14} className="mr-2" />
                        Block owner
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report this post</DialogTitle>
            <DialogDescription>
              Tell us what's wrong. An admin will review.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value.slice(0, 500))}
            placeholder="Spam, harassment, off-topic, …"
            rows={3}
            data-testid={`post-${post.id}-report-textarea`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={submitReport}
              disabled={!reportReason.trim()}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid={`post-${post.id}-report-submit`}
            >
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid={`post-${post.id}-history-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="duotone" className="text-gold" />
              Edit history
            </DialogTitle>
            <DialogDescription>
              Audit log of every change made to this post. Admin only.
            </DialogDescription>
          </DialogHeader>
          {history === null && (
            <p className="text-sm text-[#6B6B6B] py-4">Loading…</p>
          )}
          {history && history.entries && history.entries.length === 0 && (
            <p className="text-sm text-[#6B6B6B] py-4" data-testid="history-empty">
              This post hasn't been edited.
            </p>
          )}
          {history && history.entries && history.entries.length > 0 && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="history-entries">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">
                Current
              </div>
              <div className="rounded-lg border border-[#0F8043]/30 bg-[#F1FAF3] p-3">
                <p className="text-sm whitespace-pre-wrap text-ink">{history.post?.text}</p>
                <p className="text-[10px] text-[#6B6B6B] mt-1.5 font-mono">
                  Latest revision · {history.post?.updated_at ? new Date(history.post.updated_at).toLocaleString() : ""}
                </p>
              </div>
              {[...history.entries].reverse().map((e) => (
                <div key={e.id} className="rounded-lg border border-[#EAEAEA] p-3" data-testid={`history-entry-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B]">
                      Was → became
                    </span>
                    <span className="text-[10px] text-[#9A9A9A] font-mono">
                      {new Date(e.edited_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-[12px] text-[#6B6B6B] line-through whitespace-pre-wrap">
                    {e.previous_text}
                  </p>
                  <p className="text-sm whitespace-pre-wrap mt-1.5 text-ink">{e.new_text}</p>
                  <p className="text-[11px] text-[#6B6B6B] mt-2">
                    Edited by{" "}
                    <strong className="text-ink">{e.editor_name || e.editor_email || e.edited_by}</strong>
                    {e.edited_by_role === "admin" && (
                      <span className="ml-1 inline-flex items-center bg-gold/15 text-[#8A6A00] text-[9px] font-bold uppercase tracking-[0.15em] rounded-full px-1.5 py-0.5">
                        admin
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="rounded-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // Capture the prefill values ONCE on mount so the URL-cleanup below
  // doesn't strip them before the Composer reads them.
  const [prefill] = useState(() => ({
    dogId: params.get("dog_id") || "",
    text: params.get("text") || "",
  }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const [ownedDogs, setOwnedDogs] = useState([]);
  const [scope, setScope] = useState("all");
  const [prompt, setPrompt] = useState(null);
  const [topPost, setTopPost] = useState(null);
  const [followSet, setFollowSet] = useState(new Set());

  const toggleFollow = async (authorId) => {
    try {
      const { data } = await api.post(`/posts/follows/${authorId}`);
      setFollowSet((prev) => {
        const next = new Set(prev);
        if (data.following) next.add(authorId);
        else next.delete(authorId);
        return next;
      });
      toast.success(data.following ? "Following — their posts will show in your Following tab" : "Unfollowed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Pawprints community-guidelines acceptance. We fetch once on mount; if
  // null, the *first* post submission triggers a gate dialog. After accept,
  // subsequent posts skip it. State machine kept simple — a Promise resolver
  // stashed in a ref lets `requireGuidelinesAccept()` cleanly await the
  // dialog outcome.
  const [termsAccepted, setTermsAccepted] = useState(null); // null = unknown, false = not yet, true = done
  const [gateOpen, setGateOpen] = useState(false);
  const [gateChecked, setGateChecked] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);
  const gateResolverRef = useRef(null);

  useEffect(() => {
    api.get("/me/pawprints-terms")
      .then((r) => setTermsAccepted(!!r.data?.accepted_at))
      .catch(() => setTermsAccepted(false));
  }, []);

  const requireGuidelinesAccept = useCallback(() => {
    if (termsAccepted) return Promise.resolve(true);
    setGateChecked(false);
    setGateOpen(true);
    return new Promise((resolve) => {
      gateResolverRef.current = resolve;
    });
  }, [termsAccepted]);

  const handleGateConfirm = async () => {
    if (!gateChecked) return;
    setGateBusy(true);
    try {
      await api.post("/me/pawprints-terms/accept");
      setTermsAccepted(true);
      setGateOpen(false);
      gateResolverRef.current?.(true);
      gateResolverRef.current = null;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGateBusy(false);
    }
  };

  const handleGateCancel = () => {
    setGateOpen(false);
    gateResolverRef.current?.(false);
    gateResolverRef.current = null;
  };

  // Strip the prefill params from the URL after capture so a browser
  // refresh doesn't keep reapplying them on top of the user's draft.
  useEffect(() => {
    if (prefill.dogId || prefill.text) {
      const cleaned = new URLSearchParams(params);
      cleaned.delete("dog_id");
      cleaned.delete("text");
      setParams(cleaned, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (before, scopeOverride) => {
    try {
      const { data } = await api.get("/posts", {
        params: { limit: 20, scope: scopeOverride ?? scope, ...(before ? { before } : {}) },
      });
      setPosts((prev) => (before ? [...prev, ...data] : data));
      setExhausted(data.length < 20);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const switchScope = (next) => {
    if (next === scope) return;
    setScope(next);
    setLoading(true);
    setPosts([]);
    load(null, next);
  };

  useEffect(() => {
    load();
    api.get("/posts/prompts/current").then((r) => setPrompt(r.data)).catch(() => {});
    api.get("/posts/follows/mine").then((r) => {
      const mine = new Set(r.data.following || []);
      setFollowSet(mine);
      // Deep link from a "new follower" notification → offer follow-back.
      const params = new URLSearchParams(window.location.search);
      const fb = params.get("follow_back");
      const fbName = params.get("follow_back_name") || "them";
      if (fb && !mine.has(fb)) {
        toast(`Follow ${fbName} back?`, {
          duration: 10000,
          action: { label: "Follow back", onClick: () => toggleFollow(fb) },
        });
      }
    }).catch(() => {});
    api.get("/posts/top/week").then((r) => setTopPost(r.data?.post || null)).catch(() => {});
    api
      .get("/dogs")
      .then((r) => {
        const mine = (r.data || []).filter((d) => d.created_by === user?.id);
        setOwnedDogs(mine);
      })
      .catch(() => setOwnedDogs([]));
  }, [load, user?.id]);

  const onPosted = (newPost) => {
    setPosts((p) => [newPost, ...p]);
  };
  const onPostChanged = (id, updated) => {
    setPosts((p) => (updated === null ? p.filter((x) => x.id !== id) : p.map((x) => (x.id === id ? updated : x))));
  };

  const loadMore = () => {
    if (exhausted || posts.length === 0) return;
    load(posts[posts.length - 1].created_at);
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">The Dog's Voice</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">From the dogs themselves</h1>
          <p className="text-[#6B6B6B] mt-2">
            Posts are written from each dog's perspective. Like the ones that make you smile —
            but no replies, this is the dogs' page.
          </p>
          <p className="text-xs text-[#6B6B6B] mt-3">
            By posting you agree to the{" "}
            <a
              href="/terms#community"
              data-testid="feed-community-guidelines-link"
              className="underline underline-offset-2 hover:text-ink"
            >
              The Dog's Voice community guidelines
            </a>
            : no harassment, no politics, no negativity targeted at other
            members. See a violation? Tap Report.
          </p>
        </div>
        {prompt && (
          <div className="mb-4 rounded-xl border border-[#C9A227]/40 bg-[#FFFBEF] p-4 flex items-start gap-3" data-testid="feed-prompt-card">
            <PawPrint size={20} weight="duotone" className="text-[#C9A227] mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A6D00]">{prompt.override ? "Special event" : "This week's prompt"}</p>
              <p className="font-bold text-ink text-[15px]">{prompt.title}</p>
              <p className="text-[13px] text-[#6B6B6B]">{prompt.body}</p>
            </div>
            <Button
              size="sm"
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white shrink-0"
              onClick={() => window.location.assign(`/feed?text=${encodeURIComponent(prompt.tag + " ")}`)}
              data-testid="feed-prompt-post-btn"
            >
              Post yours
            </Button>
          </div>
        )}
        {topPost && (
          <a
            href={`/p/${topPost.id}`}
            className="mb-4 flex items-center gap-3 rounded-xl border border-black/10 bg-white p-3 hover:border-[#C9A227] transition-colors"
            data-testid="feed-top-post-banner"
          >
            <Trophy size={18} weight="fill" className="text-[#C9A227] shrink-0" />
            <p className="text-[13px] text-ink truncate">
              <span className="font-bold">Top post this week:</span>{" "}
              {topPost.dog?.name} — {topPost.like_count} reaction{topPost.like_count === 1 ? "" : "s"}
            </p>
          </a>
        )}
        <div className="mb-4 flex gap-1 rounded-full bg-[#F2F2F2] p-1 w-fit" data-testid="feed-scope-tabs">
          {[["all", "All dogs"], ["following", "Following"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => switchScope(key)}
              className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                scope === key ? "bg-white text-ink shadow-sm" : "text-[#6B6B6B] hover:text-ink"
              }`}
              data-testid={`feed-scope-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Composer
          ownedDogs={ownedDogs}
          onPosted={onPosted}
          prefillDogId={prefill.dogId}
          prefillText={prefill.text}
          requireGuidelinesAccept={requireGuidelinesAccept}
        />
        {loading ? (
          <div className="text-center text-[#6B6B6B] py-10">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center">
            <PawPrint size={36} weight="duotone" className="text-gold mx-auto mb-3" />
            <h3 className="font-spectral text-2xl mb-1">Be the first to post</h3>
            <p className="text-[#6B6B6B] text-sm">
              The feed is empty. Post something on behalf of one of your dogs above.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                currentUser={user}
                onChanged={(updated) => onPostChanged(p.id, updated)}
                isFollowing={followSet.has(p.author_user_id)}
                onToggleFollow={toggleFollow}
              />
            ))}
            {!exhausted && (
              <div className="text-center pt-4">
                <Button variant="outline" className="rounded-full" onClick={loadMore} data-testid="feed-load-more-btn">
                  Load older posts
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* First-time-poster gate. Triggered the first time a user submits a
          post if `pawprints_terms_accepted_at` is null on their user doc. */}
      <Dialog open={gateOpen} onOpenChange={(o) => !o && handleGateCancel()}>
        <DialogContent className="max-w-lg" data-testid="pawprints-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral text-2xl">The Dog's Voice — community guidelines</DialogTitle>
            <DialogDescription>
              The Dog's Voice is for celebrating dogs and breeding work, not for
              tearing other members down. Before your first post we just need
              you to confirm you've read the rules.
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-[#3F3F3F] space-y-2 mt-2">
            <li className="flex items-start gap-2"><span className="text-gold font-bold">·</span><span><strong>No harassment</strong> or personal attacks on other members or kennels.</span></li>
            <li className="flex items-start gap-2"><span className="text-gold font-bold">·</span><span><strong>No politics</strong> or off-topic divisive content.</span></li>
            <li className="flex items-start gap-2"><span className="text-gold font-bold">·</span><span><strong>No negativity campaigns</strong> against other breeders or dogs.</span></li>
            <li className="flex items-start gap-2"><span className="text-gold font-bold">·</span><span><strong>No misinformation</strong> about other kennels' practices.</span></li>
            <li className="flex items-start gap-2"><span className="text-gold font-bold">·</span><span>Use the <strong>Report</strong> button on anything that crosses a line.</span></li>
          </ul>
          <p className="text-xs text-[#6B6B6B] mt-3">
            Read the full rules anytime at{" "}
            <a href="/terms#community" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-ink" data-testid="pawprints-gate-full-rules">
              Terms of Service §5
            </a>
            . Violations are enforced (warning → suspension → permanent ban).
          </p>
          <label className="flex items-start gap-3 mt-4 p-3 bg-[#FBF7EC] border border-[#E8D9A6] rounded-lg cursor-pointer" data-testid="pawprints-gate-agree-row">
            <input
              type="checkbox"
              checked={gateChecked}
              onChange={(e) => setGateChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#0E0E0E]"
              data-testid="pawprints-gate-checkbox"
            />
            <span className="text-sm text-ink">
              I agree to follow The Dog's Voice community guidelines and
              understand that violations may result in suspension or
              permanent removal from the feed.
            </span>
          </label>
          <DialogFooter className="mt-2 gap-2">
            <Button
              variant="outline"
              onClick={handleGateCancel}
              className="rounded-full"
              data-testid="pawprints-gate-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGateConfirm}
              disabled={!gateChecked || gateBusy}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="pawprints-gate-confirm"
            >
              {gateBusy ? "Saving…" : "Agree & post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
